import { motion } from 'motion/react'
import type { ShopLanguage, ShopMessageKey } from '../../../i18n'
import SelectCustom, { type SelectOption } from '../../../components/SelectCustom'
import { customerMotionTransition } from '../customer-area'
import {
  ORDER_STATUS_FILTER_OPTIONS,
  type OrderDateFilterOption,
  type OrderVisualStatus,
  toVisualStatusLabel,
} from './ordersListShared'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type OrdersFiltersPanelProps = {
  reducedMotion: boolean
  language: ShopLanguage
  dateFilter: OrderDateFilterOption
  customDateFrom: string
  customDateTo: string
  statusFilters: OrderVisualStatus[]
  priceMin: string
  priceMax: string
  dateFilterOptions: SelectOption[]
  filteredCount: number
  onDateFilterChange: (value: OrderDateFilterOption) => void
  onCustomDateFromChange: (value: string) => void
  onCustomDateToChange: (value: string) => void
  onToggleStatusFilter: (status: OrderVisualStatus, checked: boolean) => void
  onPriceMinChange: (value: string) => void
  onPriceMaxChange: (value: string) => void
  onClearAllFilters: () => void
  t: TranslateFn
}

export const OrdersFiltersPanel = ({
  reducedMotion,
  language,
  dateFilter,
  customDateFrom,
  customDateTo,
  statusFilters,
  priceMin,
  priceMax,
  dateFilterOptions,
  filteredCount,
  onDateFilterChange,
  onCustomDateFromChange,
  onCustomDateToChange,
  onToggleStatusFilter,
  onPriceMinChange,
  onPriceMaxChange,
  onClearAllFilters,
  t,
}: OrdersFiltersPanelProps) => (
  <motion.div
    initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
    animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
    exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
    transition={customerMotionTransition}
    className="ca-theme-transition mt-4 rounded-xl border border-(--color-border-default) bg-(--color-bg-surface-muted) p-3.5"
  >
    <div className="grid gap-3 xl:grid-cols-3">
      <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
        {t('customer.orders.filters.date.label')}
        <SelectCustom
          value={dateFilter}
          onChange={(value) => onDateFilterChange(value as OrderDateFilterOption)}
          ariaLabel={t('customer.orders.filters.date.label')}
          options={dateFilterOptions}
          size="sm"
          buttonClassName="h-9 rounded-[10px] text-sm"
        />
      </label>

      {dateFilter === 'custom' ? (
        <div className="grid gap-2 sm:grid-cols-2 xl:col-span-2">
          <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
            {t('customer.orders.filters.date.from')}
            <input
              type="date"
              value={customDateFrom}
              onChange={(event) => onCustomDateFromChange(event.target.value)}
              className="ca-input h-9 px-3 text-sm"
            />
          </label>
          <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
            {t('customer.orders.filters.date.to')}
            <input
              type="date"
              value={customDateTo}
              onChange={(event) => onCustomDateToChange(event.target.value)}
              className="ca-input h-9 px-3 text-sm"
            />
          </label>
        </div>
      ) : null}
    </div>

    <div className="mt-3 grid gap-3 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
      <fieldset className="ca-theme-transition rounded-xl border border-(--color-border-default) bg-(--color-bg-card) p-3">
        <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-(--color-text-muted)">
          {t('customer.orders.filters.status.label')}
        </legend>
        <div className="mt-1 grid gap-1 sm:grid-cols-2">
          {ORDER_STATUS_FILTER_OPTIONS.map((status) => {
            const checked = statusFilters.includes(status)
            return (
              <label
                key={status}
                className="ca-theme-transition inline-flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-(--color-text-secondary) hover:bg-(--color-bg-surface-muted)"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(event) => onToggleStatusFilter(status, event.target.checked)}
                  className="h-4 w-4 rounded border-(--color-border-strong) text-(--color-action-primary) focus:ring-[color-mix(in_srgb,var(--color-focus-ring)_35%,transparent)]"
                />
                <span>{toVisualStatusLabel(status, language)}</span>
              </label>
            )
          })}
        </div>
      </fieldset>

      <fieldset className="ca-theme-transition rounded-xl border border-(--color-border-default) bg-(--color-bg-card) p-3">
        <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-(--color-text-muted)">
          {t('customer.orders.filters.price.label')}
        </legend>
        <div className="mt-1 grid gap-2 sm:grid-cols-2">
          <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
            {t('customer.orders.filters.price.min')}
            <input
              value={priceMin}
              onChange={(event) => onPriceMinChange(event.target.value)}
              inputMode="decimal"
              placeholder={t('customer.orders.filters.price.minPlaceholder')}
              className="ca-input h-9 px-3 text-sm"
            />
          </label>
          <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
            {t('customer.orders.filters.price.max')}
            <input
              value={priceMax}
              onChange={(event) => onPriceMaxChange(event.target.value)}
              inputMode="decimal"
              placeholder={t('customer.orders.filters.price.maxPlaceholder')}
              className="ca-input h-9 px-3 text-sm"
            />
          </label>
        </div>
      </fieldset>
    </div>

    <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
      <p className="text-xs font-medium text-(--color-text-secondary)">
        {t('customer.orders.resultsCount', { count: filteredCount })}
      </p>
      <motion.button
        type="button"
        onClick={onClearAllFilters}
        whileTap={reducedMotion ? undefined : { scale: 0.985 }}
        transition={customerMotionTransition}
        className="ca-btn-secondary inline-flex h-8 items-center px-2.5 text-xs font-semibold"
      >
        {t('customer.orders.filters.reset')}
      </motion.button>
    </div>
  </motion.div>
)
